import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { Loading, Logo } from '../components/utility/Utility';
import '../assets/css/views/invite.css';

const API_BASE = 'https://api.amplifyloyalty.com';

function formatRole(role) {
  if (role === 'viewer') return 'Viewer';
  if (role === 'admin') return 'Admin';
  return role || 'Member';
}

function InviteShell({ title, children }) {
  return (
    <div className="login invite-page">
      <div className="form-container">
        <Logo className="login__logo" />
        <hr className="login__hr" />
        <h1 className="invite-page__title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function InviteAccept() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { authed, getJwtToken, refreshUserProfile } = useContext(AuthContext);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing invite link. Ask your team owner to resend the invitation.');
      return;
    }

    const loadMeta = async () => {
      setStatus('loading');
      try {
        if (authed) {
          const jwt = await getJwtToken();
          const res = await axios.get(`${API_BASE}/app/team/invite/preview`, {
            params: { token },
            headers: { Authorization: `Bearer ${jwt}` }
          });
          setPreview(res.data);
        } else {
          const res = await axios.get(`${API_BASE}/app/team/invite/meta`, {
            params: { token }
          });
          setPreview(res.data);
        }
        setStatus('ready');
      } catch (err) {
        setStatus('error');
        setMessage(
          err.response?.data?.message || err.message || 'Could not load invitation.'
        );
      }
    };

    loadMeta();
  }, [authed, token, getJwtToken]);

  const handleAccept = async () => {
    setActing(true);
    try {
      const jwt = await getJwtToken();
      await axios.post(
        `${API_BASE}/app/team/invite/accept`,
        { token },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      await refreshUserProfile();
      navigate('/app/product-analysis/analytics', { replace: true });
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || err.message || 'Could not accept invitation.');
      setActing(false);
    }
  };

  const handleDecline = async () => {
    setActing(true);
    try {
      const jwt = await getJwtToken();
      await axios.post(
        `${API_BASE}/app/team/invite/decline`,
        { token },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      navigate('/app/product-analysis/analytics', { replace: true });
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || err.message || 'Could not decline invitation.');
      setActing(false);
    }
  };

  const storeName = preview?.storeName || 'the team';
  const title = preview ? `Join ${preview.storeName}` : 'Join';

  const loginHref = `/login?invite_token=${encodeURIComponent(token || '')}`;
  const signUpHref = `/sign-up?invite_token=${encodeURIComponent(token || '')}`;

  if (!token) {
    return (
      <InviteShell title="Invalid invite">
        <p className="invite-page__subtitle">{message}</p>
        <Link to="/login" className="btn gradient">
          Go to login
        </Link>
      </InviteShell>
    );
  }

  if (!authed) {
    return (
      <InviteShell title={status === 'ready' && preview ? title : 'Join'}>
        {status === 'loading' && <Loading />}
        {status === 'ready' && preview && (
          <>
            <p className="invite-page__subtitle">
              You&apos;ve been invited as a <strong>{formatRole(preview.role)}</strong>.
              Sign in or create an account with the email that received this invite.
            </p>
            <div className="invite-page__actions">
              <Link to={loginHref} className="btn gradient">
                Log in
              </Link>
              <Link to={signUpHref} className="btn invite-page__btn-secondary">
                Create account
              </Link>
            </div>
          </>
        )}
        {status === 'error' && (
          <p className="form-errors" role="alert">
            {message}
          </p>
        )}
      </InviteShell>
    );
  }

  return (
    <InviteShell title={status === 'ready' && preview ? title : 'Join'}>
      {status === 'loading' && <Loading />}
      {status === 'ready' && preview && (
        <>
          <p className="invite-page__subtitle">
            You&apos;ve been invited to join <strong>{storeName}</strong> as a{' '}
            <strong>{formatRole(preview.role)}</strong>.
          </p>
          <div className="invite-page__actions">
            <button
              type="button"
              className="btn gradient"
              disabled={acting}
              onClick={handleAccept}
            >
              Accept
            </button>
            <button
              type="button"
              className="btn invite-page__btn-secondary"
              disabled={acting}
              onClick={handleDecline}
            >
              Decline
            </button>
          </div>
        </>
      )}
      {status === 'error' && (
        <>
          <p className="form-errors" role="alert">
            {message}
          </p>
          <Link to="/app/product-analysis/analytics" className="btn gradient" style={{ marginTop: '1rem' }}>
            Go to app
          </Link>
        </>
      )}
    </InviteShell>
  );
}
